import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Linkedin,
  Mail,
  MessageSquare,
  Phone,
  Globe,
  Calendar,
  Inbox,
  Sparkles,
  Send,
  Loader2,
  ArrowRight,
  TrendingUp
} from 'lucide-react';
import EmbeddedModule from './EmbeddedModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { key: 'all', label: 'All', icon: Inbox },
  { key: 'meeting', label: 'Meetings', icon: Calendar },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { key: 'website', label: 'Website', icon: Globe },
  { key: 'inquiry', label: 'Inquiry', icon: Inbox },
  { key: 'ask-sanjeev', label: 'Ask Sanjeev AI', icon: Sparkles }
];

const STAGE_COLOR = {
  new: 'bg-gray-100 text-gray-700',
  contacted: 'bg-blue-100 text-blue-700',
  qualified: 'bg-amber-100 text-amber-800',
  'meeting-scheduled': 'bg-violet-100 text-violet-700',
  converted: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-rose-100 text-rose-700'
};

const LeadGenerationHub = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [channel, setChannel] = useState('all');
  const [q, setQ] = useState('');

  // Ask Sanjeev AI state
  const [askQ, setAskQ] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askAnswer, setAskAnswer] = useState(null);
  // Session id stays in sessionStorage (cleared on tab close, no PII).
  const [sessionId] = useState(() => {
    const k = 'sanmiggrc-sanjeev-session-id';
    try {
      let s = window.sessionStorage.getItem(k);
      if (!s) {
        s = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        window.sessionStorage.setItem(k, s);
      }
      return s;
    } catch {
      return `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
  });

  const refresh = useCallback(() => {
    axios.get(`${API}/leads-hub`).then((r) => setLeads(r.data)).catch(() => setLeads([]));
    axios.get(`${API}/leads-hub/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    return leads
      .filter((l) => (channel === 'all' ? true : l.channel === channel))
      .filter((l) => {
        const s = q.trim().toLowerCase();
        if (!s) return true;
        return [l.name, l.company, l.interest, l.summary].join(' ').toLowerCase().includes(s);
      });
  }, [leads, channel, q]);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!askQ.trim()) return;
    setAskLoading(true);
    setAskAnswer(null);
    try {
      const res = await axios.post(`${API}/ask-sanjeev`, {
        question: askQ,
        session_id: sessionId
      });
      setAskAnswer(res.data);
      refresh();
    } catch (err) {
      setAskAnswer({ answer: 'Could not reach Sanjeev AI right now.', priority: 'cold' });
    } finally {
      setAskLoading(false);
    }
  };

  const owner = stats?.owner;
  const waLink = owner?.whatsapp ? `https://wa.me/${owner.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hi Sanjeev, reaching out about Sanmiggrc.')}` : '#';
  const mailLink = owner?.email ? `mailto:${owner.email}?subject=${encodeURIComponent('Inquiry — Sanmiggrc Technologies')}` : '#';

  return (
    <EmbeddedModule
      eyebrow="Growth · Multi-channel"
      title="Lead Generation Hub"
      tagline="Inbound and outbound leads from meetings, email, WhatsApp, LinkedIn, the website, the inquiry module and Ask Sanjeev AI — unified, scored and routed to the right specialist."
      badgeText="Single point of contact · India"
      accentClass="from-fuchsia-700 via-pink-700 to-rose-700"
      testId="lead-hub-page"
      kpis={[
        { label: 'Total Leads', value: stats?.total ?? '—' },
        { label: 'Conversion', value: stats ? `${stats.conversion_rate}%` : '—' },
        { label: 'Channels', value: stats ? Object.keys(stats.by_channel).length : '—' },
        { label: 'Owner', value: owner?.name?.split(' ')[0] || 'Sanjeev' }
      ]}
    >
      {/* Contact strip */}
      <Card data-testid="contact-strip">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Contact Sanjeev Bhardwaj</span>
            <Badge variant="outline">Founder & CEO</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <a href={mailLink} data-testid="contact-email" className="border border-gray-200 rounded-lg p-3 hover:border-fuchsia-300 hover:bg-fuchsia-50 transition-all text-center">
              <Mail className="w-5 h-5 mx-auto text-fuchsia-700 mb-1" />
              <p className="text-xs font-medium">Email</p>
            </a>
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="border border-gray-200 rounded-lg p-3 hover:border-emerald-300 hover:bg-emerald-50 transition-all text-center">
              <MessageSquare className="w-5 h-5 mx-auto text-emerald-700 mb-1" />
              <p className="text-xs font-medium">WhatsApp</p>
            </a>
            <a href={owner?.linkedin || '#'} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50 transition-all text-center">
              <Linkedin className="w-5 h-5 mx-auto text-blue-700 mb-1" />
              <p className="text-xs font-medium">LinkedIn</p>
            </a>
            <a href={owner?.calendar || '#'} target="_blank" rel="noopener noreferrer" data-testid="contact-calendar" className="border border-gray-200 rounded-lg p-3 hover:border-violet-300 hover:bg-violet-50 transition-all text-center">
              <Calendar className="w-5 h-5 mx-auto text-violet-700 mb-1" />
              <p className="text-xs font-medium">Book Call</p>
            </a>
            <a href={`tel:${owner?.whatsapp || ''}`} data-testid="contact-phone" className="border border-gray-200 rounded-lg p-3 hover:border-amber-300 hover:bg-amber-50 transition-all text-center">
              <Phone className="w-5 h-5 mx-auto text-amber-700 mb-1" />
              <p className="text-xs font-medium">Phone</p>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Ask Sanjeev AI */}
      <Card data-testid="ask-sanjeev-card" className="border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-pink-50">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-fuchsia-700" />
            <span>Ask Sanjeev AI</span>
            <Badge className="bg-fuchsia-100 text-fuchsia-800">Pilot</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAsk} className="space-y-3">
            <Textarea
              data-testid="ask-sanjeev-input"
              rows={2}
              placeholder="Ask anything about RBI, DPDP, AML, AI governance, FIU-IND, I4C, FRM..."
              value={askQ}
              onChange={(e) => setAskQ(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-gray-500">Rule-based pilot. Your question is logged as a lead.</p>
              <Button type="submit" disabled={askLoading || !askQ.trim()} data-testid="ask-sanjeev-submit">
                {askLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Asking</> : <><Send className="w-4 h-4 mr-2" />Ask</>}
              </Button>
            </div>
          </form>
          {askAnswer && (
            <div className="mt-4 bg-white border border-fuchsia-200 rounded-lg p-4" data-testid="ask-sanjeev-answer">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {askAnswer.priority === 'hot' && (
                  <Badge className="bg-rose-600 text-white animate-pulse">Hot Lead</Badge>
                )}
                {askAnswer.priority === 'warm' && (
                  <Badge className="bg-amber-100 text-amber-800">Warm</Badge>
                )}
                {askAnswer.used_llm && (
                  <Badge className="bg-violet-100 text-violet-700 text-[10px] inline-flex items-center">
                    <Sparkles className="w-3 h-3 mr-0.5" />Claude Sonnet 4.5
                  </Badge>
                )}
                {askAnswer.lead_ref && (
                  <span className="text-[11px] font-mono text-gray-500">{askAnswer.lead_ref}</span>
                )}
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{askAnswer.answer}</p>
              {askAnswer.priority === 'hot' && owner?.whatsapp && (
                <a
                  href={`https://wa.me/${owner.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hi Sanjeev — ref ' + askAnswer.lead_ref + '\n' + askQ)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ask-sanjeev-whatsapp-cta"
                  className="inline-flex items-center mt-3 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                  WhatsApp Sanjeev about this
                </a>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Channel filters + search */}
      <Card data-testid="lead-board">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-fuchsia-700" />
              <span>Lead Board</span>
              <Badge variant="secondary">{filtered.length}</Badge>
            </CardTitle>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, company, interest..."
              className="h-9 w-72"
              data-testid="lead-search"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              const count = c.key === 'all' ? stats?.total : stats?.by_channel?.[c.key] || 0;
              const active = channel === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`channel-${c.key}`}
                  onClick={() => setChannel(c.key)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                    active ? 'bg-fuchsia-700 text-white border-fuchsia-700' : 'bg-white text-gray-700 border-gray-200 hover:border-fuchsia-300'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {c.label}
                  {count !== undefined && (
                    <span className={`text-[10px] px-1.5 rounded-full ${active ? 'bg-white/20' : 'bg-gray-100'}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((lead) => (
              <div
                key={lead.id}
                data-testid={`lead-${lead.lead_ref}`}
                className="border border-gray-200 rounded-lg p-4 bg-white hover:border-fuchsia-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] font-mono">{lead.lead_ref}</Badge>
                    <Badge className={`text-[10px] ${STAGE_COLOR[lead.stage] || 'bg-gray-100 text-gray-700'}`}>{lead.stage}</Badge>
                  </div>
                  <span className="text-[11px] text-gray-500">Score {lead.score}</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{lead.name}</p>
                <p className="text-xs text-gray-600">{lead.designation}{lead.company ? ` · ${lead.company}` : ''}</p>
                <p className="text-xs text-gray-700 mt-2"><span className="text-gray-500">Interest:</span> {lead.interest}</p>
                <p className="text-xs text-gray-600 mt-1.5">{lead.summary}</p>
                <div className="flex items-center justify-between mt-3">
                  <Badge variant="outline" className="text-[10px] capitalize">{lead.channel}</Badge>
                  <span className="text-[11px] text-gray-500">{lead.last_touch}</span>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6 col-span-full">No leads match.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </EmbeddedModule>
  );
};

export default LeadGenerationHub;
